import { isValidPhoneNumber, parsePhoneNumberWithError } from "libphonenumber-js"
import type { TComposerMode } from "@/components/generic/TemplatesComposer2/types"
import { prettifyPhone, getUSStateFromPhoneNumber } from "./phone"

/**
 * Extract and prettify the contact data based on the provided parameters.
 *
 * @param {Object} options - The options object.
 * @param {string | Array<string>} options.emails - The email(s) associated with the contact.
 * @param {string} options.email - The primary email associated with the contact.
 * @param {string | Array<string>} options.phoneNumbers - The phone number(s) associated with the contact.
 * @param {string} options.phoneNumber - The primary phone number associated with the contact.
 * @param {string} options.fullName - The full name of the contact.
 * @param {string} options.fallback - The fallback name to use if the full name is not provided.
 * @param {"sms" | "email"} options.mode - The mode to determine how to prettify the contact name.
 * @param {boolean} options.showInvalidValues - Whether to show invalid values.
 * @returns {Object} The prettifed contact data.
 */
export function extractPrettyContactData({
  emails,
  email,
  phoneNumbers,
  phoneNumber,
  fullName,
  fallback,
  mode,
  showInvalidValues,
}: {
  emails?: string | Array<string> | null
  email?: string | null
  phoneNumbers?: string | Array<string> | null
  phoneNumber?: string | null
  fullName?: string | null
  fallback?: string
  mode: TComposerMode
  showInvalidValues?: boolean
}): {
  contactEmail: string
  contactPhoneNumber: string
  contactFullName: string
  avatarText?: string
} {
  // Extract the email
  let contactEmail: string = ""
  const _contactEmails: Array<string> = typeof emails === "string" ? (emails ?? "").split(",") : (emails ?? [])

  if ((email ?? "").trim() !== "") {
    contactEmail = (email ?? "").trim()
  } else if (_contactEmails.length > 0 && _contactEmails[0].trim() !== "") {
    contactEmail = _contactEmails[0].trim()
  }

  // Extract the phone
  let contactPhoneNumber: string = ""

  const _contactPhoneNumbers: Array<string> = typeof phoneNumbers === "string" ? (phoneNumbers ?? "").split(",") : Array.isArray(phoneNumbers) ? (phoneNumbers ?? []) : []

  if ((phoneNumber ?? "").trim() !== "") {
    contactPhoneNumber = (phoneNumber ?? "").trim()
  } else if (_contactPhoneNumbers.length > 0 && typeof _contactPhoneNumbers[0] === "string" && _contactPhoneNumbers[0].trim() !== "") {
    contactPhoneNumber = _contactPhoneNumbers[0].trim()
  }

  // Extract the full name
  let contactFullName = fallback ?? "Unknown name"
  let avatarText: string | undefined

  if (fullName && fullName?.trim() !== "") {
    const trimmedFullName = fullName.trim()
    if (isValidPhoneNumber(trimmedFullName, { defaultCountry: "US" })) {
      contactFullName = prettifyPhone({
        phone: trimmedFullName,
        showInvalidValues,
      })
      // For phone numbers, we'll use the first two letters of the US state if it's a US number
      try {
        const phoneNumber = parsePhoneNumberWithError(trimmedFullName, {
          defaultCountry: "US",
        })
        if (phoneNumber.country === "US") {
          const state = getUSStateFromPhoneNumber(trimmedFullName)
          avatarText = state ?? phoneNumber.formatNational().slice(0, 2)

          // If in the international format, the number is toll free, we show TF
          if (phoneNumber.formatInternational().replace(/\s/g, "").startsWith("+1800")) {
            avatarText = "TF"
          }
        } else {
          avatarText = phoneNumber.formatInternational().slice(0, 2)
        }
      } catch (err) {
        console.error("Error parsing phone number for avatar text", err)
      }
    } else {
      contactFullName = trimmedFullName
      // For non-phone numbers, if the name is not made of two words, we will return the first two letters of the name
      // Otherwise, we will return the first letter of each word
      avatarText = trimmedFullName.includes(" ")
        ? trimmedFullName
            .split(" ")
            .map((name) => name[0])
            .join("")
        : trimmedFullName.slice(0, 2).toUpperCase()
    }
  } else {
    switch (mode) {
      case "sms":
        if (contactPhoneNumber) {
          contactFullName = prettifyPhone({
            phone: contactPhoneNumber,
            showInvalidValues,
          })
          // For phone numbers, we'll use the first two letters of the US state if it's a US number
          try {
            const phoneNumber = parsePhoneNumberWithError(contactPhoneNumber, {
              defaultCountry: "US",
            })
            if (phoneNumber.country === "US") {
              const state = getUSStateFromPhoneNumber(contactPhoneNumber)
              avatarText = state ?? phoneNumber.formatNational().slice(0, 2)
            } else {
              avatarText = phoneNumber.formatInternational().slice(0, 2)
            }
          } catch (err) {
            console.error("Error parsing phone number for avatar text", err)
          }
        }
        break
      case "email":
      default:
        if (contactEmail) {
          contactFullName = contactEmail
          // For emails, use first two letters
          avatarText = contactEmail.slice(0, 2).toUpperCase()
        }
        break
    }
  }

  return {
    contactEmail,
    contactPhoneNumber,
    contactFullName,
    avatarText,
  }
}
